import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { Section, Table, td, tdR, th, thR } from '../components/reports/blocks';
import { downloadReport } from '../components/reports/download';
import { istanbulToday } from '../components/reports/period';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { minorToInput, toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { DisputablePayments, DisputeDetail, DisputeList } from '../lib/types-faz8';

/**
 * Ödeme itirazları / ters ibraz (F8-13, ADR-811). Kayıt bankanın veya ödeme kuruluşunun bildirimine göre girilir;
 * sağlayıcıya bir şey gönderilmez. Rehber: docs/faz-8/REPORTS_FINANCE_TR.md#odeme-itirazlari.
 */

type Status = 'open' | 'evidence_submitted' | 'won' | 'lost' | 'accepted';
const TONE: Record<Status, Tone> = {
  open: 'amber',
  evidence_submitted: 'slate',
  won: 'moss',
  lost: 'rose',
  accepted: 'rose',
};

function StatusBadge({ status }: { status: Status }) {
  const t = useT();
  return <Badge tone={TONE[status]}>{t(`admin.disputes.statuses.${status}`)}</Badge>;
}

export function PaymentDisputesPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate, formatNumber } = useI18n();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'active' | 'closed' | 'all'>('active');
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ['payment-disputes', filter],
    queryFn: () =>
      api.get<DisputeList>(`/v1/admin/payment-disputes${filter === 'all' ? '' : `?status=${filter}`}`),
  });
  const list = q.data ?? [];
  const open = list.filter((x) => x.status === 'open' || x.status === 'evidence_submitted');
  return (
    <PageFrame
      helpKey="page.payment_disputes"
      actions={
        can('payments.dispute.manage') && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            {t('admin.disputes.newDispute')}
          </Button>
        )
      }
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.disputes.statusFilter')} help={<HelpTip k="disputes.status" />}>
          {(p) => (
            <Select
              {...p}
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'active' | 'closed' | 'all')}
            >
              {(['active', 'closed', 'all'] as const).map((f) => (
                <option key={f} value={f}>
                  {t(`admin.disputes.filters.${f}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {filter === 'active' && q.data && open.length > 0 && (
          <p className="pb-2 text-sm text-ink-2" role="status">
            {t('admin.disputes.summary', { count: formatNumber(open.length, 0) })}
          </p>
        )}
      </div>
      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !list.length ? (
        <EmptyState
          title={filter === 'active' ? t('admin.disputes.emptyActive') : t('admin.disputes.empty')}
        />
      ) : (
        <Table
          caption={t('admin.disputes.tableTitle')}
          head={
            <tr>
              {(['number', 'order', 'kind', 'status'] as const).map((c) => (
                <th key={c} scope="col" className={th}>
                  {t(`admin.disputes.cols.${c}`)}
                </th>
              ))}
              <th scope="col" className={thR}>
                {t('admin.disputes.cols.amount')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.disputes.cols.fee')}
              </th>
              {(['opened', 'due', 'closed'] as const).map((c) => (
                <th key={c} scope="col" className={th}>
                  {t(`admin.disputes.cols.${c}`)}
                </th>
              ))}
            </tr>
          }
        >
          {list.map((x) => (
            <tr key={x.id} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-medium`}>
                <Link to="/odeme-itirazlari/$id" params={{ id: x.id }} className="text-pine hover:underline">
                  {x.number}
                </Link>
              </th>
              <td className={td}>{x.orderNumber}</td>
              <td className={td}>{t(`admin.disputes.kinds.${x.kind}`)}</td>
              <td className={td}>
                <StatusBadge status={x.status} />
              </td>
              <td className={tdR}>{money(x.amountMinor, x.currency)}</td>
              <td className={tdR}>{x.feeMinor ? money(x.feeMinor, x.currency) : '—'}</td>
              <td className={td}>{formatDate(x.openedOn, false)}</td>
              <td className={td}>
                {x.responseDueOn ? formatDate(x.responseDueOn, false) : '—'}
                {x.overdue && (
                  <Badge tone="rose" className="ms-2">
                    {t('admin.disputes.overdue')}
                  </Badge>
                )}
                {x.dueSoon && (
                  <Badge tone="amber" className="ms-2">
                    {t('admin.disputes.dueSoon')}
                  </Badge>
                )}
              </td>
              <td className={td}>{x.closedOn ? formatDate(x.closedOn, false) : '—'}</td>
            </tr>
          ))}
        </Table>
      )}
      {creating && (
        <DisputeDialog
          onClose={() => setCreating(false)}
          onSaved={(id) => void navigate({ to: '/odeme-itirazlari/$id', params: { id } })}
        />
      )}
    </PageFrame>
  );
}

/** Yeni itiraz (sipariş numarasıyla ödeme bulunur) veya mevcut itirazın düzeltilmesi. */
function DisputeDialog({
  edit,
  onClose,
  onSaved,
}: {
  edit?: DisputeDetail;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const [idem] = useState(() => new IdempotencyKey());
  const [orderNumber, setOrderNumber] = useState('');
  const [search, setSearch] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState(edit?.paymentId ?? '');
  const [kind, setKind] = useState<'chargeback' | 'inquiry'>(edit?.kind ?? 'chargeback');
  const [caseNumber, setCaseNumber] = useState(edit?.caseNumber ?? '');
  const [reasonCode, setReasonCode] = useState(edit?.reasonCode ?? '');
  const [reason, setReason] = useState(edit?.reason ?? '');
  const [amount, setAmount] = useState(edit ? minorToInput(edit.amountMinor) : '');
  const [fee, setFee] = useState(edit && edit.feeMinor ? minorToInput(edit.feeMinor) : '');
  const [openedOn, setOpenedOn] = useState(edit?.openedOn ?? istanbulToday());
  const [dueOn, setDueOn] = useState(edit?.responseDueOn ?? '');
  const [note, setNote] = useState(edit?.note ?? '');
  const pays = useQuery({
    queryKey: ['disputable-payments', search],
    queryFn: () =>
      api.get<DisputablePayments>(
        `/v1/admin/payment-disputes/payments?orderNumber=${encodeURIComponent(search!)}`,
      ),
    enabled: !!search && !edit,
  });
  const selected = pays.data?.find((p) => p.id === paymentId);
  const currency = edit?.currency ?? selected?.currency ?? 'TRY';
  const amountMinor = toMinor(amount, currency);
  const feeMinor = fee.trim() ? toMinor(fee, currency) : 0;
  const save = useApiMutation(
    () => {
      const body = {
        kind,
        caseNumber: caseNumber.trim() || null,
        reasonCode: reasonCode.trim() || null,
        reason: reason.trim() || null,
        amountMinor: amountMinor!,
        feeMinor: feeMinor!,
        openedOn,
        responseDueOn: dueOn || null,
        note: note.trim() || null,
      };
      return edit
        ? api.patch<{ id: string }>(`/v1/admin/payment-disputes/${edit.id}`, body, { ifMatch: edit.version })
        : api.post<{ id: string }>(
            '/v1/admin/payment-disputes',
            { ...body, paymentId },
            { idempotencyKey: idem },
          );
    },
    {
      invalidate: [['payment-disputes'], ['payment-dispute', edit?.id], ['finance-report']],
      success: edit ? t('admin.disputes.saved') : t('admin.disputes.created'),
      onSuccess: (r) => {
        onClose();
        onSaved?.(r.id);
      },
      silentFieldErrors: true,
    },
  );
  const money$ = (s: string, m: number | null, field: string) =>
    s && m === null ? t('admin.disputes.amountInvalid') : fieldError(save.error, field);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={edit ? t('admin.disputes.editTitle') : t('admin.disputes.dialogTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!paymentId || amountMinor === null || feeMinor === null || !openedOn}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!edit && (
          <>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setPaymentId('');
                setSearch(orderNumber.trim() || null);
              }}
            >
              <Field label={t('admin.disputes.orderNumber')} required>
                {(p) => <Input {...p} value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />}
              </Field>
              <Button type="submit" disabled={!orderNumber.trim()}>
                {t('admin.disputes.findPayments')}
              </Button>
            </form>
            {pays.isFetching && <Spinner label={t('common.loading')} />}
            {pays.data && !pays.data.length && (
              <p className="text-sm text-ink-3">{t('admin.disputes.noPayments')}</p>
            )}
            {pays.data && pays.data.length > 0 && (
              <fieldset>
                <legend className="mb-1 text-sm font-medium text-ink">{t('admin.disputes.payment')}</legend>
                {pays.data.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="dispute-payment"
                      checked={paymentId === p.id}
                      onChange={() => {
                        setPaymentId(p.id);
                        if (!amount) setAmount(minorToInput(Math.max(0, p.amountMinor - p.disputedMinor)));
                      }}
                    />
                    {t('admin.disputes.paymentOption', {
                      provider: t(`admin.disputes.providers.${p.provider}`),
                      amount: money(p.amountMinor, p.currency),
                      date: p.capturedAt ? formatDate(p.capturedAt, false) : '—',
                    })}
                    {p.disputedMinor > 0 && (
                      <span className="text-ink-3">
                        ·{' '}
                        {t('admin.disputes.alreadyDisputed', { amount: money(p.disputedMinor, p.currency) })}
                      </span>
                    )}
                  </label>
                ))}
              </fieldset>
            )}
          </>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.disputes.kind')}>
            {(p) => (
              <Select
                {...p}
                value={kind}
                onChange={(e) => setKind(e.target.value as 'chargeback' | 'inquiry')}
              >
                <option value="chargeback">{t('admin.disputes.kinds.chargeback')}</option>
                <option value="inquiry">{t('admin.disputes.kinds.inquiry')}</option>
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.disputes.caseNumber')}
            optionalLabel={t('common.optional')}
            error={fieldError(save.error, 'caseNumber')}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={80}
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={`${t('admin.disputes.amount')} (${currency})`}
            required
            help={<HelpTip k="disputes.amount" />}
            error={money$(amount, amountMinor, 'amountMinor')}
          >
            {(p) => (
              <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            )}
          </Field>
          <Field
            label={`${t('admin.disputes.fee')} (${currency})`}
            optionalLabel={t('common.optional')}
            error={money$(fee, feeMinor, 'feeMinor')}
          >
            {(p) => <Input {...p} inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />}
          </Field>
          <Field label={t('admin.disputes.openedOn')} required error={fieldError(save.error, 'openedOn')}>
            {(p) => (
              <Input {...p} type="date" value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} />
            )}
          </Field>
          <Field
            label={t('admin.disputes.responseDueOn')}
            optionalLabel={t('common.optional')}
            error={fieldError(save.error, 'responseDueOn')}
          >
            {(p) => <Input {...p} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}
          </Field>
          <Field label={t('admin.disputes.reasonCode')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input
                {...p}
                maxLength={40}
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('admin.disputes.reason')} optionalLabel={t('common.optional')}>
            {(p) => (
              <Input {...p} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Field>
        </div>
        <Field label={t('admin.disputes.note')} optionalLabel={t('common.optional')}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <p className="rounded-md border border-line bg-surface-2 p-3 text-xs text-ink-2">
          {t('admin.disputes.providerNote')}
        </p>
      </div>
    </Dialog>
  );
}

export function PaymentDisputeDetailPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/odeme-itirazlari/$id' });
  const [moving, setMoving] = useState<Status | null>(null);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const q = useQuery({
    queryKey: ['payment-dispute', id],
    queryFn: () => api.get<DisputeDetail>(`/v1/admin/payment-disputes/${id}`),
  });
  const invalidate = [['payment-dispute', id], ['payment-disputes'], ['finance-report']];
  const addNote = useApiMutation(
    () => api.post(`/v1/admin/payment-disputes/${id}/notes`, { note: note.trim() }),
    { invalidate, success: t('admin.disputes.noteAdded'), onSuccess: () => setNote('') },
  );
  const upload = useApiMutation(
    (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload(`/v1/admin/payment-disputes/${id}/documents`, form);
    },
    {
      invalidate,
      success: t('admin.disputes.uploaded'),
      onSuccess: () => {
        if (fileRef.current) fileRef.current.value = '';
      },
    },
  );
  if (q.isError)
    return (
      <PageFrame helpKey="page.payment_dispute">
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      </PageFrame>
    );
  if (!q.data)
    return (
      <PageFrame helpKey="page.payment_dispute">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const d = q.data;
  const statusName = (s: string | null) => (s ? t(`admin.disputes.statuses.${s}`) : '—');
  const documents = d.events.filter((e) => e.type === 'document' && e.fileId);
  const info = [
    {
      label: t('admin.disputes.order'),
      value: (
        <Link to="/siparisler/$id" params={{ id: d.orderId }} className="text-pine hover:underline">
          {d.orderNumber}
        </Link>
      ),
    },
    {
      label: t('admin.disputes.paymentInfo'),
      value: (
        <>
          {t('admin.disputes.paymentLine', {
            provider: t(`admin.disputes.providers.${d.provider}`),
            amount: money(d.paymentAmountMinor, d.currency),
          })}
          {d.refundedMinor > 0 && (
            <span className="ms-2 text-ink-3">
              {t('admin.disputes.refunded', { amount: money(d.refundedMinor, d.currency) })}
            </span>
          )}
        </>
      ),
    },
    { label: t('admin.disputes.kind'), value: t(`admin.disputes.kinds.${d.kind}`) },
    { label: t('admin.disputes.amount'), value: money(d.amountMinor, d.currency) },
    { label: t('admin.disputes.fee'), value: d.feeMinor ? money(d.feeMinor, d.currency) : '—' },
    { label: t('admin.disputes.caseNumber'), value: d.caseNumber ?? '—' },
    { label: t('admin.disputes.reasonCode'), value: d.reasonCode ?? '—' },
    { label: t('admin.disputes.reason'), value: d.reason ?? '—' },
    { label: t('admin.disputes.openedOn'), value: formatDate(d.openedOn, false) },
    {
      label: t('admin.disputes.responseDueOn'),
      value: d.responseDueOn ? formatDate(d.responseDueOn, false) : '—',
    },
    { label: t('admin.disputes.cols.closed'), value: d.closedOn ? formatDate(d.closedOn, false) : '—' },
    { label: t('admin.disputes.note'), value: d.note ?? '—' },
  ];
  return (
    <PageFrame
      helpKey="page.payment_dispute"
      title={t('admin.disputes.detailTitle', { number: d.number })}
      actions={d.canManage && <Button onClick={() => setEditing(true)}>{t('admin.disputes.edit')}</Button>}
    >
      <p className="-mt-4 text-sm">
        <Link to="/odeme-itirazlari" className="text-pine hover:underline">
          ← {t('admin.disputes.back')}
        </Link>
      </p>
      <Section title={t('admin.disputes.actionsTitle')} help={<HelpTip k="disputes.status" />}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={d.status} />
          {d.canManage &&
            d.transitions.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === 'won' ? 'primary' : s === 'lost' || s === 'accepted' ? 'danger' : 'secondary'}
                onClick={() => setMoving(s)}
              >
                {t(`admin.disputes.moveTo.${s}`)}
              </Button>
            ))}
        </div>
      </Section>
      <Section title={t('admin.disputes.infoTitle')}>
        <KeyValue items={info} />
      </Section>
      <Section title={t('admin.disputes.documentsTitle')} help={<HelpTip k="disputes.documents" />}>
        {!documents.length ? (
          <p className="text-sm text-ink-3">{t('admin.disputes.noDocuments')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {documents.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-2">
                <span>{e.note}</span>
                <span className="text-ink-3">· {formatDate(e.createdAt)}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('admin.disputes.downloadNamed', { name: e.note ?? '' })}
                  onClick={() =>
                    void downloadReport(
                      `/v1/admin/payment-disputes/${id}/documents/${e.fileId}`,
                      e.note ?? 'belge',
                    )
                  }
                >
                  {t('admin.reportFiles.download')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {d.canManage && (
          <div className="mt-3">
            <label className="text-sm font-medium text-ink" htmlFor="dispute-file">
              {t('admin.disputes.upload')}
            </label>
            <input
              id="dispute-file"
              ref={fileRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="mt-1 block text-sm"
              disabled={upload.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
              }}
            />
            <p className="mt-1 text-xs text-ink-3">{t('admin.disputes.uploadHint')}</p>
          </div>
        )}
      </Section>
      {d.canManage && (
        <Section title={t('admin.disputes.notesTitle')}>
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (note.trim()) addNote.mutate(undefined);
            }}
          >
            <Field label={t('admin.disputes.note')}>
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  maxLength={2000}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <Button type="submit" size="sm" loading={addNote.isPending} disabled={!note.trim()}>
              {t('admin.disputes.addNote')}
            </Button>
          </form>
        </Section>
      )}
      <Section title={t('admin.disputes.historyTitle')}>
        <ol className="space-y-2 text-sm">
          {d.events.map((e) => (
            <li key={e.id} className="border-s-2 border-line ps-3">
              <p className="font-medium text-ink">
                {e.type === 'status'
                  ? t('admin.disputes.events.status', {
                      from: statusName(e.fromStatus),
                      to: statusName(e.toStatus),
                    })
                  : e.type === 'document'
                    ? t('admin.disputes.events.document', { name: e.note ?? '' })
                    : t(`admin.disputes.events.${e.type}`)}
              </p>
              {e.note && e.type !== 'document' && <p className="whitespace-pre-line text-ink-2">{e.note}</p>}
              <p className="text-xs text-ink-3">
                {formatDate(e.createdAt)} · {e.actorName ?? t('admin.disputes.system')}
              </p>
            </li>
          ))}
        </ol>
      </Section>
      {moving && <StatusDialog d={d} status={moving} onClose={() => setMoving(null)} />}
      {editing && <DisputeDialog edit={d} onClose={() => setEditing(false)} />}
    </PageFrame>
  );
}

function StatusDialog({ d, status, onClose }: { d: DisputeDetail; status: Status; onClose: () => void }) {
  const t = useT();
  const [idem] = useState(() => new IdempotencyKey());
  const closing = status === 'won' || status === 'lost' || status === 'accepted';
  const [closedOn, setClosedOn] = useState(istanbulToday());
  const [note, setNote] = useState('');
  const save = useApiMutation(
    () =>
      api.post(
        `/v1/admin/payment-disputes/${d.id}/status`,
        { status, ...(closing ? { closedOn } : {}), note: note.trim() || null },
        { ifMatch: d.version, idempotencyKey: idem },
      ),
    {
      invalidate: [['payment-dispute', d.id], ['payment-disputes'], ['finance-report']],
      success: t('admin.disputes.statusChanged'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.disputes.statusDialog', { status: t(`admin.disputes.statuses.${status}`) })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={status === 'open' && !note.trim()}
            onClick={() => save.mutate(undefined)}
          >
            {t(`admin.disputes.moveTo.${status}`)}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {closing && (
          <Field label={t('admin.disputes.closedOn')} required error={fieldError(save.error, 'closedOn')}>
            {(p) => (
              <Input {...p} type="date" value={closedOn} onChange={(e) => setClosedOn(e.target.value)} />
            )}
          </Field>
        )}
        <Field
          label={t('admin.disputes.statusNote')}
          required={status === 'open'}
          optionalLabel={status === 'open' ? undefined : t('common.optional')}
          error={fieldError(save.error, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {status === 'open' && <p className="text-xs text-ink-3">{t('admin.disputes.reopenNote')}</p>}
        {(status === 'lost' || status === 'accepted') && (
          <p className="text-xs text-ink-2">{t('admin.disputes.lossNote')}</p>
        )}
      </div>
    </Dialog>
  );
}
